import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GoalManager from '../goals/GoalManager';
import apiService from '../../services/api';

/**
 * GoalDialogs Component
 * Manages all goal-related dialogs and their state
 * 
 * @param {Object} props - Component props
 * @param {string} props.token - Authentication token
 * @param {string} props.currentConversationId - Current conversation ID
 * @param {Object} props.socket - Socket.io connection
 * @param {Object} props.user - User object
 * @param {Function} props.onSystemMessage - Callback to add system messages
 * @param {Function} props.triggerAIAfterGoalAction - Callback to trigger AI response
 * @returns {JSX.Element} GoalDialogs component
 */
const GoalDialogs = React.forwardRef(({
  token,
  currentConversationId,
  socket,
  user,
  onSystemMessage,
  triggerAIAfterGoalAction,
  pauseAutoplay,
  resumeAutoplay
}, ref) => {
  // Goal States
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [pendingGoalSuggestion, setPendingGoalSuggestion] = useState(null);
  const [goalSuggestionDialogOpen, setGoalSuggestionDialogOpen] = useState(false);
  const [therapistGoalSuggestion, setTherapistGoalSuggestion] = useState(null);
  const [showTherapistGoalDialog, setShowTherapistGoalDialog] = useState(false);
  
  // Track last suggestion to prevent duplicates
  const lastSuggestionRef = useRef(null);
  const lastTherapistGoalRef = useRef(null);

  // Goal Event Handlers
  const handleOpenGoalModal = () => {
    setGoalModalOpen(true);
    if (pauseAutoplay) pauseAutoplay('goal-modal');
  };
  const handleCloseGoalModal = () => {
    setGoalModalOpen(false);
    if (resumeAutoplay) resumeAutoplay('goal-modal');
  };

  const handleConfirmGoalCreation = async () => {
    if (!pendingGoalSuggestion || !token) return;

    try {
      await apiService.createGoal({
        description: pendingGoalSuggestion.description,
        goalType: pendingGoalSuggestion.goalType || 'one_off'
      }, token);

      const successMessage = {
        senderRole: 'system',
        text: `Goal added: "${pendingGoalSuggestion.description}"`,
        timestamp: new Date()
      };
      onSystemMessage(successMessage);

      if (triggerAIAfterGoalAction) {
        triggerAIAfterGoalAction("Goal created successfully.");
      }
    } catch (error) {
      console.error('Failed to create goal:', error);
      const errorMessage = {
        senderRole: 'system',
        text: 'Failed to create goal. Please try again.',
        timestamp: new Date()
      };
      onSystemMessage(errorMessage);
    }

    setGoalSuggestionDialogOpen(false);
    setPendingGoalSuggestion(null);
    if (resumeAutoplay) resumeAutoplay('goal-suggestion');
  };

  const handleRejectGoalCreation = () => {
    setGoalSuggestionDialogOpen(false);
    setPendingGoalSuggestion(null);
    if (resumeAutoplay) resumeAutoplay('goal-suggestion');

    const rejectionMessage = {
      senderRole: 'system',
      text: "Okay, goal not added.",
      timestamp: new Date()
    };
    onSystemMessage(rejectionMessage);

    if (triggerAIAfterGoalAction) {
      triggerAIAfterGoalAction("Goal suggestion declined.");
    }
  };

  const handleConfirmTherapistGoal = async () => {
    if (!therapistGoalSuggestion || !socket || !currentConversationId || !user) return;

    try {
      await apiService.createGoal({
        description: therapistGoalSuggestion.description,
        goalType: therapistGoalSuggestion.goalType || 'one_off'
      }, token);

      const successMessage = {
        senderRole: 'system',
        text: `Goal added: "${therapistGoalSuggestion.description}"`,
        timestamp: new Date()
      };
      onSystemMessage(successMessage);

      // Notify therapist via socket
      socket.emit('client_action_on_therapist_goal', {
        conversationId: currentConversationId,
        goalId: therapistGoalSuggestion.goalId,
        action: 'confirmed'
      });
    } catch (error) {
      console.error('Failed to create therapist goal:', error);
      const errorMessage = {
        senderRole: 'system',
        text: 'Failed to create goal. Please try again.',
        timestamp: new Date()
      };
      onSystemMessage(errorMessage);
    }

    setShowTherapistGoalDialog(false);
    setTherapistGoalSuggestion(null);
    if (resumeAutoplay) resumeAutoplay('therapist-goal');
  };

  const handleRejectTherapistGoal = () => {
    if (!therapistGoalSuggestion || !socket || !currentConversationId) return;

    const rejectionMessage = {
      senderRole: 'system',
      text: `Goal "${therapistGoalSuggestion.description}" declined.`,
      timestamp: new Date()
    };
    onSystemMessage(rejectionMessage);

    // Notify therapist via socket
    socket.emit('client_action_on_therapist_goal', {
      conversationId: currentConversationId,
      goalId: therapistGoalSuggestion.goalId,
      action: 'rejected'
    });

    setShowTherapistGoalDialog(false);
    setTherapistGoalSuggestion(null);
    if (resumeAutoplay) resumeAutoplay('therapist-goal');
  };

  // Expose methods to parent component via ref
  React.useImperativeHandle(ref, () => ({
    openGoalModal: handleOpenGoalModal,
    // "Not now" on the inline goal card (ST-20260909-0002): same outcome as
    // declining in the dialog, without opening it first.
    declineGoalSuggestion: () => handleRejectGoalCreation(),
    showGoalSuggestion: (suggestion) => {
      // Check if this is the same suggestion as last time (prevent duplicates)
      // Extended to 5 seconds to handle cases where AI re-suggests same goal
      if (lastSuggestionRef.current &&
          lastSuggestionRef.current.description === suggestion.description &&
          lastSuggestionRef.current.goalType === suggestion.goalType &&
          (Date.now() - lastSuggestionRef.current.timestamp) < 5000) {
        console.log('[DUPLICATE GOAL] Ignoring duplicate goal suggestion within 5 seconds');
        return;
      }

      lastSuggestionRef.current = {
        description: suggestion.description,
        goalType: suggestion.goalType,
        timestamp: Date.now()
      };

      setPendingGoalSuggestion(suggestion);
      setGoalSuggestionDialogOpen(true);
      if (pauseAutoplay) pauseAutoplay('goal-suggestion');
    },
    handleTherapistGoalConfirmation: (data) => {
      if (data.conversationId === currentConversationId) {
        // Check for duplicate therapist goal confirmations (defense in depth)
        if (lastTherapistGoalRef.current &&
            lastTherapistGoalRef.current.description === data.description &&
            (Date.now() - lastTherapistGoalRef.current.timestamp) < 5000) {
          console.log('[DUPLICATE GOAL] Ignoring duplicate therapist goal confirmation within 5 seconds');
          return;
        }

        lastTherapistGoalRef.current = {
          description: data.description,
          timestamp: Date.now()
        };

        setTherapistGoalSuggestion({
          goalId: data.goalId,
          description: data.description,
          goalType: data.goalType,
          message: data.message,
          proposedBy: data.proposedBy
        });
        setShowTherapistGoalDialog(true);
        if (pauseAutoplay) pauseAutoplay('therapist-goal');
      }
    }
  }));

  return (
    <>
      {/* Goal Manager Modal */}
      <Dialog
        open={goalModalOpen}
        onClose={handleCloseGoalModal}
        aria-labelledby="goal-manager-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="goal-manager-dialog-title">
          Manage Therapeutic Goals
          <IconButton
            aria-label="close"
            onClick={handleCloseGoalModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {goalModalOpen && <GoalManager />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGoalModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* AI Goal Suggestion Dialog */}
      <Dialog
        open={goalSuggestionDialogOpen}
        // Only the buttons decide. A click outside or Escape used to decline
        // the goal and tell the AI the client had said no (ST-20260915-0007).
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleRejectGoalCreation();
        }}
        aria-labelledby="goal-suggestion-dialog-title"
      >
        <DialogTitle id="goal-suggestion-dialog-title">Confirm Goal</DialogTitle>
        <DialogContent>
          <Typography>
            The AI suggests adding the following goal based on your conversation:
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            {pendingGoalSuggestion?.description}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Would you like to add this to your goals list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectGoalCreation} color="secondary">
            No, Thanks
          </Button>
          <Button onClick={handleConfirmGoalCreation} variant="contained" autoFocus>
            Yes, Add Goal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Therapist Goal Suggestion Dialog */}
      <Dialog
        open={showTherapistGoalDialog}
        // Same for a therapist's goal: an outside click used to tell the
        // therapist the client had rejected it.
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleRejectTherapistGoal();
        }}
        aria-labelledby="therapist-goal-suggestion-dialog-title"
      >
        <DialogTitle id="therapist-goal-suggestion-dialog-title">
          Confirm Goal from Therapist
        </DialogTitle>
        <DialogContent>
          <Typography>
            {therapistGoalSuggestion?.message || "Your therapist suggests adding the following goal:"}
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            {therapistGoalSuggestion?.description}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Type: {therapistGoalSuggestion?.goalType === 'one_off' ? 'One-off Task' : 'Habit'}
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Would you like to add this to your goals list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectTherapistGoal} color="secondary">
            No, Thanks
          </Button>
          <Button onClick={handleConfirmTherapistGoal} variant="contained" autoFocus>
            Yes, Add This Goal
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default GoalDialogs;